import { Component, OnInit, AfterViewInit, ViewChild, ChangeDetectorRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { debounceTime, distinctUntilChanged } from 'rxjs';

export interface JobCodeDialogResult {
  jobCode: string;
  isManual: boolean;
  workOrderId?: number;
}

@Component({
  selector: 'app-job-code-dialog',
  templateUrl: './job-code-dialog.component.html',
  styleUrl: './job-code-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class JobCodeDialogComponent implements OnInit, AfterViewInit {
  data = inject(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<JobCodeDialogComponent>>(MatDialogRef);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;

  // Mode: 'list' or 'add'
  viewMode: 'list' | 'add' = 'list';

  // Controls
  searchControl = new FormControl('');
  manualJobCodeControl = new FormControl('', Validators.required);

  // Data
  allWorkOrders: any[] = [];
  filteredWorkOrders: any[] = [];
  currentJobCode = '';
  isLoading = false;

  constructor() {
    const data = this.data;

    if (data?.workOrderList) {
      this.allWorkOrders = data.workOrderList;
      this.filteredWorkOrders = [...this.allWorkOrders];
    }
    if (data?.currentJobCode) {
      this.currentJobCode = data.currentJobCode;
    }
    // Set loading state from parent
    this.isLoading = data?.isLoading ?? false;
  }

  ngOnInit(): void {
    // Setup search listener
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((search) => {
      this.filterList(search || '');
    });
  }

  ngAfterViewInit(): void {
    // Force viewport to recalculate size after dialog is fully rendered
    this.refreshViewport();
  }

  private refreshViewport(): void {
    // Use multiple attempts to ensure the viewport is properly initialized
    setTimeout(() => {
      if (this.viewport) {
        this.viewport.checkViewportSize();
        this.cdr.detectChanges();
      }
      // Second attempt with longer delay for slow renders
      setTimeout(() => {
        if (this.viewport) {
          this.viewport.checkViewportSize();
          this.cdr.detectChanges();
        }
      }, 100);
    }, 50);
  }

  filterList(search: string): void {
    const term = search.toLowerCase();
    if (!term) {
      this.filteredWorkOrders = [...this.allWorkOrders];
    } else {
      this.filteredWorkOrders = this.allWorkOrders.filter(wo => 
        wo.woCode?.toLowerCase().includes(term)
      );
    }
    // Refresh viewport after filtering
    this.refreshViewport();
  }

  // TrackBy function for virtual scroll performance
  trackByJobCode(index: number, item: any): string | number {
    return item.id || item.woCode || index;
  }

  // Switch to Manual Add Mode
  switchToAddMode(): void {
    this.viewMode = 'add';
    // Reset list to show all items
    this.filteredWorkOrders = [...this.allWorkOrders];
    
    // Use current search term as initial value if it's not a match?
    const currentSearch = this.searchControl.value;
    if (currentSearch) {
      this.manualJobCodeControl.setValue(currentSearch);
    } else {
      this.manualJobCodeControl.setValue('');
    }
  }

  // Switch back to List Mode
  switchToListMode(): void {
    this.viewMode = 'list';
    this.manualJobCodeControl.setValue('');
  }

  // Select existing job code
  selectJobCode(item: any): void {
    const result: JobCodeDialogResult = {
      jobCode: item.woCode,
      isManual: false,
      workOrderId: item.id
    };
    this.dialogRef.close(result);
  }

  // Add Manual Job Code
  addJobCode(): void {
    if (this.manualJobCodeControl.invalid) {
      this.manualJobCodeControl.markAsTouched();
      return;
    }

    const result: JobCodeDialogResult = {
      jobCode: this.manualJobCodeControl.value?.trim() || '',
      isManual: true,
      workOrderId: 0
    };
    this.dialogRef.close(result);
  }

  close(): void {
    this.dialogRef.close(null);
  }
}
